import { PushNotifications } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import { persistentLogger } from '@/lib/persistent-logger';

export const setupPushNotifications = async () => {
  // Verificar se está em plataforma nativa
  if (!Capacitor.isNativePlatform()) {
    logger.info('Push notifications only available on native platforms');
    return;
  }

  try {
    // Solicitar permissão
    let permStatus = await PushNotifications.checkPermissions();
    
    persistentLogger.addLog('PUSH', 'info', 'Checking push notification permissions', {
      current: permStatus.receive
    });
    
    if (permStatus.receive === 'prompt') {
      permStatus = await PushNotifications.requestPermissions();
      persistentLogger.addLog('PUSH', 'info', 'Push permission requested', {
        granted: permStatus.receive === 'granted'
      });
    }
    
    if (permStatus.receive !== 'granted') {
      persistentLogger.addLog('PUSH', 'warn', 'Push notification permission denied by user');
      throw new Error('User denied permissions!');
    }
    
    await PushNotifications.register();
    persistentLogger.addLog('PUSH', 'info', 'Push notifications registered');
    
    // Listener para registration
    await PushNotifications.addListener('registration', async (token) => {
      logger.info('Push registration success', { token: token.value });
      persistentLogger.addLog('PUSH', 'info', 'Push token registered', {
        tokenPreview: token.value.substring(0, 20) + '...',
        platform: Capacitor.getPlatform()
      });
      
      // Salvar token no Supabase
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        await supabase
          .from('push_tokens')
          .upsert({
            user_id: session.user.id,
            token: token.value,
            platform: Capacitor.getPlatform(),
            updated_at: new Date().toISOString()
          }, {
            onConflict: 'user_id,platform'
          });
        persistentLogger.addLog('PUSH', 'info', 'Push token saved to backend');
      }
    });
    
    // Listener para erros de registration
    await PushNotifications.addListener('registrationError', (error) => {
      logger.error('Error on push registration', error);
      persistentLogger.addLog('PUSH', 'error', 'Push registration error', { error });
    });
    
    // Listener para notificações recebidas (app em foreground)
    await PushNotifications.addListener('pushNotificationReceived', (notification) => {
      logger.info('Push notification received', { notificationId: notification.id });
      persistentLogger.addLog('PUSH', 'info', 'Push notification received (foreground)', {
        id: notification.id,
        title: notification.title
      });
    });
    
    // Listener para tap em notificação
    await PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
      logger.info('Push notification action performed', { 
        notificationId: notification.notification.id,
        actionId: notification.actionId 
      });
      persistentLogger.addLog('PUSH', 'info', 'Push notification tapped', {
        id: notification.notification.id,
        actionId: notification.actionId,
        type: notification.notification.data?.type
      });
      
      // Navegar para rota específica se houver
      const data = notification.notification.data;
      if (data?.route) {
        window.location.href = data.route;
      } else if (data?.type === 'workout_reminder') {
        window.location.href = '/training';
      } else if (data?.type === 'chat_message') {
        window.location.href = '/training';
      }
    });
    
    logger.info('Push notifications setup complete');
  } catch (error) {
    logger.error('Error setting up push notifications', error);
  }
};

export const removePushNotifications = async () => {
  if (!Capacitor.isNativePlatform()) return;
  
  try {
    await PushNotifications.removeAllListeners();
    
    const { data: { session } } = await supabase.auth.getSession();
    if (session) {
      await supabase
        .from('push_tokens')
        .delete()
        .eq('user_id', session.user.id)
        .eq('platform', Capacitor.getPlatform());
    }
  } catch (error) {
    logger.error('Error removing push notifications', error);
  }
};
